<template>
  <AdminLayout>
  <div class="d-flex flex-column">
    <div class="d-flex flex-row align-center mb-3">
      <h1 class="outfit-regular">Username Creator</h1>
      <v-spacer></v-spacer>
      <v-btn icon @click="openRealmsDialogAndSelectPath" :disabled="loading" dark color="black" title="Select an Existing Realm">
        <v-icon>mdi-book-open-blank-variant</v-icon>
      </v-btn>
    </div>
    <v-progress-linear :value="progressValue" stream color="yellow darken-3" class="mt-2 mb-8"></v-progress-linear>

    <v-alert v-if="errorMessage" type="error" dense class="my-4" text>{{ errorMessage }}</v-alert>

    <!-- ---------------------- Simplified User Input ---------------------- -->
    <v-text-field
        v-model="username"
        label="Enter your Username (e.g., Astra Nova)"
        filled
        dense
        :disabled="loading"
        hide-details
        class="mb-4"
        outlined
    ></v-text-field>

    <v-text-field
        v-model="newRealmEntry.idea"
        label="Enter a NEW Realm Idea (e.g., Futuristic cyberpunk)"
        filled
        dense
        :disabled="loading"
        hide-details
        outlined
        class="mb-2"
        @focus="selectNewRealmPath"
        @input="handleNewRealmIdeaInput"
        placeholder="Describe your realm vision here..."
    ></v-text-field>

    <div class="d-flex align-center my-2">
        <v-divider></v-divider>
        <span class="text-overline mx-3">OR</span>
        <v-divider></v-divider>
    </div>

    <div class="d-flex align-center mb-4">
        <v-btn @click="openRealmsDialogAndSelectPath" :disabled="loading" dark color="black" class="flex-grow-1">
            <v-icon left>mdi-book-open-blank-variant</v-icon>
            Select Existing Realm
        </v-btn>
    </div>
     <div v-if="currentPath === 'existing' && selectedExistingRealm" class="mb-4 pa-3 grey lighten-4 rounded text-center">
        Selected Realm: <strong class="deep-purple--text">{{ selectedExistingRealm.name }}</strong> ({{ selectedExistingRealm.concept ? selectedExistingRealm.concept.substring(0,50)+'...' : 'No concept'}})
    </div>


    <v-btn @click="startSimplifiedProcess" :loading="loading" color="yellow darken-3" block x-large class="mb-6">
        <v-icon left>mdi-creation</v-icon>
        Generate My Character & Realm
    </v-btn>
    <!-- ---------------------- End Simplified User Input ---------------------- -->


    <!-- Expansion Panels (largely unchanged, will display data from this.realm) -->
    <v-expansion-panels accordion multiple v-model="panels">
      <v-expansion-panel>
        <v-expansion-panel-header>
          <v-icon left class="flex-none mr-4" :color="realm.name ? 'green' : 'grey'">
            {{ realm.name ? 'mdi-check-circle' : 'mdi-circle-outline' }}
          </v-icon>
          <div class="text-overline">Realm Basic Info</div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="text-overline">Name</div>
          <v-sheet color="grey lighten-4" rounded class="pa-4">
            <v-text-field solo label="Name" v-model="realm.name" hide-details :readonly="!isEditMode || loading"></v-text-field>
          </v-sheet>
          <div class="text-overline mt-6">Concept</div>
          <v-sheet color="grey lighten-4" rounded class="pa-4">
            <v-textarea solo label="Concept" v-model="realm.concept" hide-details auto-grow :readonly="!isEditMode || loading"></v-textarea>
          </v-sheet>
           <div class="text-overline mt-6">Original Idea</div>
           <v-sheet color="grey lighten-4" rounded class="pa-4">
            <v-text-field solo label="Original Idea" v-model="realm.idea" hide-details readonly></v-text-field>
          </v-sheet>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header>
          <v-icon left class="flex-none mr-4" :color="realm.landscape.image || (realm.landscape.images && realm.landscape.images.length > 0) ? 'green' : 'grey'">
            {{ realm.landscape.image || (realm.landscape.images && realm.landscape.images.length > 0) ? 'mdi-check-circle' : 'mdi-circle-outline' }}
          </v-icon>
          <div class="text-overline">Realm Landscape</div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
           <div class="text-overline">Art</div>
          <v-sheet color="grey lighten-4" rounded class="pa-4">
            <div v-if="realm.landscape.image || (realm.landscape.images && realm.landscape.images.length > 0)" class="mt-4">
              <div v-if="realm.landscape.image" class="mb-4">
                <v-img :src="realm.landscape.image" contain max-height="400"></v-img>
              </div>
              <v-row v-if="realm.landscape.images && realm.landscape.images.length > 1" class="mt-4">
                <v-col v-for="(img, index) in realm.landscape.images.filter(i => i !== realm.landscape.image)" :key="`landscape-img-${index}`" cols="12" md="6">
                  <v-img :src="img" contain max-height="200" class="mb-2" @click="setMainImage(img)" :class="{ 'main-image-border': img === realm.landscape.image }"></v-img>
                </v-col>
              </v-row>
            </div>
            <div v-else-if="loading && realm.name && !realm.landscape.image" class="my-6 text-center grey--text">Generating landscape image...</div>
            <div v-else class="my-6 text-center grey--text">No landscape image generated or loaded yet.</div>
            
            <div class="d-flex justify-end mt-4">
              <v-btn 
                @click="generateAdditionalLandscapeImage" 
                :loading="generatingLandscapeImage" 
                :disabled="loading || !realm.id || !realm.name" 
                color="deep-purple" small dark
              >
                <v-icon left>mdi-image-plus</v-icon>
                Generate Another Landscape
              </v-btn>
            </div>
          </v-sheet>
        </v-expansion-panel-content>
      </v-expansion-panel>
      
      <!-- Character Panel: Will show the generated character -->
      <v-expansion-panel>
        <v-expansion-panel-header>
           <v-icon left class="flex-none mr-4" :color="realm.characters.length > 0 ? 'green' : 'grey'">
            {{ realm.characters.length > 0 ? 'mdi-check-circle' : 'mdi-circle-outline' }}
          </v-icon>
          <div class="text-overline">My Character ({{ realm.characters.length > 0 ? realm.characters.find(c => c.name === username)?.name || 'Unnamed' : 'Not Generated' }})</div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div v-if="realm.characters.length === 0 && loading && completedSteps >= 2" class="text-center grey--text my-4">Generating character...</div>
          <div v-else-if="realm.characters.length === 0" class="text-center grey--text my-4">No character generated yet for this session.</div>
          
          <div class="d-flex justify-end mb-4" v-if="realm.id">
             <v-btn 
              @click="customCharacterDialog = true" 
              :disabled="loading || !realm.id"
              color="blue-grey" 
              dark
              class="mr-2"
            >
              <v-icon left>mdi-account-edit</v-icon>
              Add Custom Character
            </v-btn>
            <v-btn 
              @click="generateNewCharacter()" 
              :loading="generatingCharacter" 
              :disabled="loading || !realm.id"
              color="deep-purple lighten-2" 
              dark
            >
              <v-icon left>mdi-account-plus</v-icon>
              Generate Another Random Character
            </v-btn>
          </div>

          <v-expansion-panels v-if="realm.characters.length > 0">
            <!-- Display only the character matching the current username, or the latest if multiple -->
            <template v-for="(character, index) in realm.characters.filter(c => c.name === username || realm.characters.indexOf(c) === realm.characters.length -1).slice(0,1)">
              <v-expansion-panel :key="`char-${index}`">
                <v-expansion-panel-header>
                  <v-avatar size="60" class="mr-4 flex-none">
                    <v-img v-if="character.avatar && character.avatar.image" :src="character.avatar.image" contain></v-img>
                    <v-icon v-else size="60">mdi-account-circle</v-icon>
                  </v-avatar>
                  <div class="text-overline">{{ character.name }} - {{ character.role }}</div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-sheet color="grey lighten-4" rounded class="pa-4">
                    <v-text-field solo label="Name" v-model="character.name" hide-details :readonly="!isEditMode || loading || character.name === username"></v-text-field>
                    <v-textarea solo label="Origin / Story" v-model="character.origin" class="mt-3" hide-details auto-grow :readonly="!isEditMode || loading"></v-textarea>
                    <v-textarea solo label="Lore / Abilities" v-model="character.lore" class="mt-3" hide-details auto-grow :readonly="!isEditMode || loading"></v-textarea>
                    <v-text-field solo label="Role" v-model="character.role" class="mt-3" hide-details :readonly="!isEditMode || loading"></v-text-field>
                    
                    <div v-if="character.avatar && character.avatar.image" class="mt-3">
                      <v-img :src="character.avatar.image" contain max-height="400"></v-img>
                    </div>
                     <v-row v-if="character.avatar && character.avatar.images && character.avatar.images.length > 1" class="mt-4">
                      <v-col v-for="(img, imgIndex) in character.avatar.images.filter(i => i !== character.avatar.image)" :key="`char-img-${index}-${imgIndex}`" cols="12" md="6">
                        <v-img 
                          :src="img" contain max-height="200" class="mb-2" 
                          @click="setMainCharacterImage(realm.characters.indexOf(character), img)" 
                          :class="{ 'main-image-border': img === character.avatar.image }"
                        ></v-img>
                      </v-col>
                    </v-row>
                    <div class="d-flex justify-end mt-4">
                      <v-btn 
                        @click="generateAdditionalCharacterImage(realm.characters.indexOf(character))" 
                        :loading="generatingCharacterImage && generatingCharacterIndex === realm.characters.indexOf(character)" 
                        :disabled="loading || !realm.id || !character.avatar || !character.avatar.prompt"
                        color="deep-purple" small dark
                      >
                        <v-icon left>mdi-image-plus</v-icon>
                        Generate Another Avatar
                      </v-btn>
                    </div>
                    <v-btn icon @click="removeCharacter(realm.characters.indexOf(character))" v-if="character.name !== username" class="mt-2"><v-icon>mdi-delete</v-icon></v-btn>
                  </v-sheet>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </template>
          </v-expansion-panels>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Optional: Realm Lore and Story panels can be kept for further detail -->
       <v-expansion-panel>
        <v-expansion-panel-header>
          <v-icon left class="flex-none mr-4" :color="realm.lore && realm.lore.short ? 'green' : 'grey'">
            {{ realm.lore && realm.lore.short ? 'mdi-check-circle' : 'mdi-circle-outline' }}
          </v-icon>
          <div class="text-overline">Realm Lore (Optional Details)</div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="text-overline">Description</div>
          <v-sheet color="grey lighten-4" rounded class="pa-4">
            <v-textarea solo label="Short Lore" v-model="realm.lore.short" hide-details auto-grow :readonly="!isEditMode || loading"></v-textarea>
            <v-textarea solo label="Detailed Lore" v-model="realm.lore.detailed" class="mt-4" hide-details auto-grow :readonly="!isEditMode || loading"></v-textarea>
          </v-sheet>
          <div class="text-overline mt-6">History</div>
          <v-sheet color="grey lighten-4" rounded class="pa-4">
            <v-textarea solo label="History" v-model="realm.lore.history" hide-details auto-grow :readonly="!isEditMode || loading"></v-textarea>
          </v-sheet>
          <div class="text-overline mt-6">Rules</div>
          <v-sheet color="grey lighten-4" rounded class="pa-4">
            <v-textarea solo label="Rules" v-model="realm.lore.rules" hide-details auto-grow :readonly="!isEditMode || loading"></v-textarea>
          </v-sheet>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header>
          <v-icon left class="flex-none mr-4" :color="realm.story && realm.story.content ? 'green' : 'grey'">
            {{ realm.story && realm.story.content ? 'mdi-check-circle' : 'mdi-circle-outline' }}
          </v-icon>
          <div class="text-overline">Realm Story (Optional)</div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
           <div class="text-overline">Content</div>
          <v-sheet color="grey lighten-4" rounded class="pa-4">
            <v-textarea solo label="Content" v-model="realm.story.content" hide-details auto-grow :readonly="!isEditMode || loading"></v-textarea>
          </v-sheet>
          
          <div class="d-flex align-center mt-6">
            <div class="text-overline flex-grow-1">Audio Narration</div>
            <v-btn 
              v-if="realm.story.content && !realm.story.audio && !loading"
              @click="generateStoryAudio" 
              :loading="generatingAudio" 
              color="deep-purple" small dark
            >
              <v-icon left>mdi-text-to-speech</v-icon>
              Generate Audio
            </v-btn>
          </div>
          
          <v-sheet v-if="realm.story.audio" color="grey lighten-4" rounded class="pa-4">
            <audio controls style="width: 100%" :src="realm.story.audio"></audio>
          </v-sheet>
          <div v-else-if="loading && realm.story.content && !realm.story.audio" class="my-6 text-center grey--text">Generating/Uploading audio...</div>
          <div v-else-if="generatingAudio" class="my-6 text-center grey--text">Generating/Uploading audio...</div>
           <div class="d-flex justify-end mt-4">
              <v-btn @click="triggerGenerateRealmStory" :loading="generatingRealmStory" :disabled="loading || !realm.id || !realm.name" color="teal" small dark>
                <v-icon left>mdi-script-text-outline</v-icon>
                Generate Full Realm Story
              </v-btn>
            </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <!-- "Update Realm" button, moved to the bottom for general save -->
      <v-btn v-if="isEditMode && realm.id" @click="updateRealm" :loading="saving" dark color="blue" class="mt-6" block>
        <v-icon left>mdi-content-save</v-icon>
        Save Changes to Realm
      </v-btn>

    </v-expansion-panels>


    <v-dialog v-model="openRealmsDialog" max-width="800">
      <v-card>
        <v-card-title>Select an Existing Realm</v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item v-for="r in realms" :key="r.id" @click="loadSelectedRealm(r.id)">
              <v-list-item-avatar size="60" tile v-if="r.landscape && r.landscape.image">
                <v-img :src="r.landscape.image" contain max-height="400"></v-img>
              </v-list-item-avatar>
               <v-list-item-avatar size="60" tile v-else>
                <v-icon size="40">mdi-image-off</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ r.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ r.concept ? r.concept.substring(0,100) + '...' : 'No concept available' }}</v-list-item-subtitle>
                 <v-list-item-subtitle>{{ r.characters ? r.characters.length : 0 }} characters</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
             <v-list-item v-if="realms.length === 0 && !loading">
                <v-list-item-content>
                    <v-list-item-title class="text-center grey--text">No realms found. Create one!</v-list-item-title>
                </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="loading">
                 <v-list-item-content class="text-center">
                    <v-progress-circular indeterminate color="deep-purple"></v-progress-circular>
                 </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="openRealmsDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <v-dialog v-model="customCharacterDialog" max-width="600">
      <v-card>
        <v-card-title>Create Custom Character</v-card-title>
        <v-card-text>
          <v-alert v-if="customCharacterError" type="error" dense text>{{ customCharacterError }}</v-alert>
          <v-form ref="customCharacterForm" @submit.prevent="generateCustomCharacter">
            <v-text-field
              v-model="customCharacter.name"
              label="Character Name"
              :rules="[v => !!v || 'Name is required']"
              required
              outlined
            ></v-text-field>
            
            <v-textarea
              v-model="customCharacter.description"
              label="Brief Description (Appearance, personality, goals, or special abilities)"
              :rules="[v => !!v || 'Description is required']"
              required
              auto-grow
              rows="3"
              outlined
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="customCharacterDialog = false">Cancel</v-btn>
          <v-btn
            color="deep-purple"
            dark
            :loading="generatingCustomCharacter"
            @click="generateCustomCharacter"
          >
            <v-icon left>mdi-account-plus</v-icon>
            Generate Character
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
  </AdminLayout>
</template>

<script>
import { collection, addDoc, doc, getDocs, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/init';
import { getIdToken, getAuth } from "firebase/auth"; // Assuming getAuth is already available via this.authUser
import { 
  getStorage,
  ref as storageFileRef, 
  uploadBytes,
  getDownloadURL,
  uploadString 
} from 'firebase/storage';


export default {
  name: 'AdminUsernames',
  components: { AdminLayout: () => import('@/layouts/Admin.vue') },
  data() {
    return {
      username: "", // New: For user's character name
      currentPath: 'new', // 'new' or 'existing'
      newRealmEntry: { idea: "" }, // For new realm idea input
      selectedExistingRealm: null, // Stores data of a realm selected from dialog

      realm: { // This will be the active realm being built/displayed
        id: null,
        idea: "", // Will be set from newRealmEntry.idea or selectedExistingRealm.idea
        name: "", concept: "",
        lore: { short: "", detailed: "", history: "", rules: "" },
        landscape: { 
          prompt: "", 
          image: "", 
          images: [] 
        },
        story: { content: "", audio: "" },
        characters: [],
      },
      loading: false, // General loading state for the main process
      saving: false, // For "Update Realm" button
      errorMessage: '',
      customCharacterError: '',
      totalSteps: 0, completedSteps: 0, progressValue: 0,
      panels: [], // Open panels
      // NUM_CHARACTERS: 5, // Not used in simplified flow, but kept for generateNewCharacter
      storage: null, 
      isEditMode: false, // True if a realm (newly created or loaded) is active
      
      // Specific loading flags for secondary actions
      generatingCharacter: false, 
      generatingAudio: false,
      generatingLandscapeImage: false,
      generatingCharacterImage: false,
      generatingCharacterIndex: -1,
      generatingRealmStory: false, // New flag for generating full realm story

      openRealmsDialog: false,
      realms: [], // List of all available realms
      
      customCharacterDialog: false,
      customCharacter: {
        name: "",
        description: "",
      },
      generatingCustomCharacter: false,
      // characterRoles: ["Hero", "Villain", ...], // Kept if needed for custom character generation
    }
  },
  
  computed: {
    pageTitle() {
      if (this.isEditMode && this.realm.name) {
        return `Realm: ${this.realm.name}`;
      }
      return 'Create Your Character & Realm';
    },
    authUser() { // Assuming authUser is provided by a mixin or global property
        return getAuth().currentUser;
    }
  },

  created() {
    this.storage = getStorage(); 
    this.checkForExistingRealmRouteParam(); // Check if navigating directly to an existing realm
    this.fetchRealms(); // Fetch all realms for the dialog
    this.resetRealmDisplay(); // Initialize with empty state
  },

  methods: {
    // --- New Simplified Flow Methods ---
    selectNewRealmPath() {
        this.currentPath = 'new';
        this.selectedExistingRealm = null; 
        // If a realm was previously loaded into this.realm, reset it for the new idea path
        if (this.realm.id && this.newRealmEntry.idea !== this.realm.idea) {
            this.resetRealmDisplay(); // Keep newRealmEntry.idea if user is typing it
            const currentTypedIdea = this.newRealmEntry.idea;
            this.realm.idea = currentTypedIdea; // but make sure it reflects in this.realm.idea
        }
    },
    handleNewRealmIdeaInput(value) {
        // If user types in the new idea field, it implies they are going for a new realm.
        // This ensures that if they had selected an existing realm, that selection is cleared.
        if (this.currentPath === 'existing' || this.selectedExistingRealm) {
            this.selectedExistingRealm = null;
        }
        this.currentPath = 'new';
        // this.newRealmEntry.idea is already updated by v-model
    },

    openRealmsDialogAndSelectPath() {
        // No need to set currentPath here, loadSelectedRealm will do it
        this.openRealmsDialog = true;
        if (!this.realms.length) { // Fetch if not already fetched
            this.fetchRealms();
        }
    },
    
    loadSelectedRealm(realmId) { // Called from dialog selection
        const realmData = this.realms.find(r => r.id === realmId);
        if (realmData) {
            this.selectedExistingRealm = JSON.parse(JSON.stringify(realmData)); // Deep copy
            this.newRealmEntry.idea = ""; // Clear new idea input
            this.currentPath = 'existing';
            this.realm.idea = realmData.idea; // Display idea of selected realm
        }
        this.openRealmsDialog = false;
    },

    resetProgressAndPanels() {
        this.completedSteps = 0;
        this.progressValue = 0;
        this.panels = [];
    },

    resetRealmDisplay() { // Resets the main this.realm object for display
        this.realm = {
            id: null, idea: this.newRealmEntry.idea, name: "", concept: "",
            lore: { short: "", detailed: "", history: "", rules: "" },
            landscape: { prompt: "", image: "", images: [] },
            story: { content: "", audio: "" },
            characters: [],
        };
        this.isEditMode = false;
        this.resetProgressAndPanels();
    },

    async startSimplifiedProcess() {
        this.errorMessage = '';
        if (!this.username.trim()) {
            this.errorMessage = "Please enter your Username.";
            return;
        }
        if (!this.authUser) {
            this.errorMessage = "You must be logged in."; return;
        }

        this.loading = true;
        this.resetProgressAndPanels();
        let realmDbRef;

        try {
            if (this.currentPath === 'new') {
                if (!this.newRealmEntry.idea.trim()) {
                    this.errorMessage = "Please enter a New Realm Idea.";
                    this.loading = false; return;
                }
                this.totalSteps = 4; // Init + Concept + Landscape + Character (LLM + Image)
                
                // 1. Initialize this.realm for the new idea
                this.resetRealmDisplay(); // Clears this.realm, sets default structure
                this.realm.idea = this.newRealmEntry.idea;
                this.panels = [0]; // Open first panel

                // 2. Create Realm Document in Firestore to get ID
                const docRef = await addDoc(collection(db, 'realms'), {
                    userId: this.authUser.uid,
                    idea: this.realm.idea,
                    name: "", concept: "", // Initialize
                    lore: { short: "", detailed: "", history: "", rules: "" },
                    landscape: { prompt: "", image: "", images: [] },
                    story: { content: "", audio: "" },
                    characters: [],
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                });
                this.realm.id = docRef.id;
                realmDbRef = doc(db, 'realms', this.realm.id);
                this.isEditMode = true; // Now it's an "editable" entity
                this.updateProgress(0); // Step 1: Init done

                // 3. Generate Realm Name & Concept
                const conceptPrompt = `
                Based on the realm idea: "${this.realm.idea}".
                Generate a compelling name (e.g., "Whisperwind Expanse", "Ironclad Dominion") and a 1-2 paragraph concept description for this new fantasy/sci-fi realm.
                The concept should outline the realm's core theme, mood, and unique characteristics.
                Output JSON: {"name": "string", "concept": "string"}
                `;
                await this._executeLlmStep(conceptPrompt, "Realm Name & Concept",
                    (data) => {
                        if (!data.name || !data.concept) throw new Error("Name/Concept JSON missing/invalid.");
                        this.realm.name = data.name;
                        this.realm.concept = data.concept;
                    },
                    () => ({ name: this.realm.name, concept: this.realm.concept }),
                    realmDbRef, 0); // Auto-open basic info panel (index 0)

                // 4. Generate Realm Landscape Image
                const landscapeLLMPrompt = `
                For the realm "${this.realm.name}" (Concept: ${this.realm.concept}), generate an image prompt for its main landscape.
                Style: A digital illustration, storybook-style, magical, soft brush textures, warm atmospheric lighting, vintage children's book look. Widescreen cinematic aspect ratio. Mood: wondrous, timeless. Avoid: cartoon, hyper-stylized, overly-dark, overly-realistic.
                Output JSON: {"image_prompt": "string detailed prompt for an image generation AI"}
                `;
                let landscapeArtPrompt = `A beautiful digital painting of ${this.realm.name}, ${this.realm.concept.substring(0,150)}. Style: storybook, magical, fantasy art, cinematic lighting.`; // Fallback
                try {
                    await this._executeLlmStep(landscapeLLMPrompt, "Landscape Prompt",
                        (data) => { if (data.image_prompt) landscapeArtPrompt = data.image_prompt; },
                        () => ({}), // No direct DB update for this sub-step
                        realmDbRef, -1); // No panel auto-open for this sub-step
                } catch (e) { console.warn("Could not generate specific landscape prompt, using fallback.", e); }

                this.realm.landscape.prompt = landscapeArtPrompt; // Save the prompt used
                await this._executeImageStep(
                    landscapeArtPrompt, "landscape",
                    (url) => {
                        this.realm.landscape.image = url || "";
                        if (url) this.realm.landscape.images = [url]; else this.realm.landscape.images = [];
                    },
                    () => ({ 
                        'landscape.prompt': this.realm.landscape.prompt,
                        'landscape.image': this.realm.landscape.image, 
                        'landscape.images': this.realm.landscape.images 
                    }),
                    realmDbRef, 1); // Auto-open landscape panel (index 1)

            } else if (this.currentPath === 'existing') {
                if (!this.selectedExistingRealm || !this.selectedExistingRealm.id) {
                    this.errorMessage = "Please select an Existing Realm.";
                    this.loading = false; return;
                }
                this.totalSteps = 2; // Load (effectively 0) + Character (LLM + Image)
                
                // 1. Load selected realm into this.realm
                this.realm = JSON.parse(JSON.stringify(this.selectedExistingRealm));
                this.isEditMode = true;
                realmDbRef = doc(db, 'realms', this.realm.id);
                this.updateProgress(0); // Step 1: Load done
                this.panels = [0, 1]; // Open basic info and landscape

                // Ensure landscape image exists if not already loaded (or if base object was minimal)
                if (!this.realm.landscape) this.realm.landscape = { prompt: "", image: "", images: [] };
                if (!this.realm.landscape.images) this.realm.landscape.images = [];
                if (this.realm.landscape.image && !this.realm.landscape.images.includes(this.realm.landscape.image)) {
                    this.realm.landscape.images.unshift(this.realm.landscape.image);
                }


            } else {
                this.errorMessage = "Invalid path selected."; this.loading = false; return;
            }

            // 5. Generate Character (for both new and existing realm paths)
            await this.generateCharacterForUser(this.username, realmDbRef);
            
            // If new realm, consider generating brief lore
            if (this.currentPath === 'new' && this.realm.name && this.realm.concept) {
                await this.generateInitialRealmLore(realmDbRef);
            }


            this.errorMessage = '';
            console.log('Simplified process completed!');
            if (!this.panels.includes(2)) this.panels.push(2); // Ensure character panel is open
            this.progressValue = 100;


        } catch (error) {
            console.error('Error in simplified process:', error);
            this.errorMessage = `Process failed: ${error.message || 'Unknown error'}`;
            if (this.currentPath === 'new' && !this.realm.name) { // If new realm creation failed early
                this.resetRealmDisplay(); // Clear partially formed new realm
            }
        } finally {
            this.loading = false;
        }
    },

    async generateCharacterForUser(characterName, realmDbRef) {
        if (!this.realm.id) {
            throw new Error("Realm ID is missing for character generation.");
        }
        console.log(`Generating character: ${characterName} for realm: ${this.realm.name}`);
        this.updateProgress(this.panels.length > 0 ? this.panels[this.panels.length-1] + 1 : 2); // Next panel for character
        
        const existingCharacter = this.realm.characters.find(c => c.name === characterName);
        if (existingCharacter) {
            console.log(`Character ${characterName} already exists in this realm. Skipping generation, will display existing.`);
            // If you want to RE-generate, you might remove the existing one first or add a suffix.
            // For now, we assume the user wants to see *their* character. If it's there, fine.
            // The prompt below is for generating a *new* one.
            // Let's filter it out for now if we are re-running on same realm
            this.realm.characters = this.realm.characters.filter(c => c.name !== characterName);
        }

        const charPrompt = `
        You are generating a character named "${characterName}" for the realm "${this.realm.name}".
        Realm Concept: ${this.realm.concept}.
        This character, ${characterName}, is the protagonist.
        Develop their origin story (2-3 paragraphs), detailed lore (2-3 paragraphs covering key traits, skills, goals, and their unique connection or role within the realm), and a primary role title (e.g., Explorer, Rebel, Scientist, Mystic, Guardian).
        The character's story and lore should be compelling, kid-friendly, and fit the realm's atmosphere.
        Output JSON: {
            "origin": "string (character's backstory)",
            "lore": "string (character's details, abilities, purpose)",
            "role": "string (character's title or archetype)",
            "avatar_prompt": "string (detailed prompt for an image AI to generate a shoulders-up portrait of ${characterName}, storybook style, matching their described origin, lore, and role. Kid-friendly. Include clothing/appearance details reflecting their role in ${this.realm.name}. Style: textured, painterly, warm, expressive. Dimensions 1024x1024.)"
        }`;

        let charIndex = -1;
        let newCharacterData = { name: characterName, origin: "", lore: "", role: "Adventurer", avatar: { prompt: "", image: "", images: [] }};

        await this._executeLlmStep(charPrompt, `Character ${characterName} Details`,
          (data) => {
            if (!data.origin || !data.lore || !data.role || !data.avatar_prompt) throw new Error("Character JSON missing keys.");
            newCharacterData.origin = data.origin;
            newCharacterData.lore = data.lore;
            newCharacterData.role = data.role;
            newCharacterData.avatar.prompt = data.avatar_prompt;
          },
          () => ({}), // DB update handled after image
          realmDbRef, 2 // Character panel index
        );
        
        // Add character to list before image generation, then update
        this.realm.characters.push(newCharacterData);
        charIndex = this.realm.characters.length - 1;

        if (newCharacterData.avatar.prompt) {
          await this._executeImageStep(
            newCharacterData.avatar.prompt,
            characterName.replace(/\s+/g, '_').toLowerCase() + "_avatar",
            (url) => {
              this.realm.characters[charIndex].avatar.image = url || "";
              if (url) this.realm.characters[charIndex].avatar.images = [url];
            },
            () => ({ characters: this.realm.characters }),
            realmDbRef, 2 // Character panel index
          );
        } else {
          console.warn(`Skipping avatar for ${characterName} due to missing prompt.`);
          await updateDoc(realmDbRef, { characters: this.realm.characters, updatedAt: serverTimestamp() }); // Save char details even if no image
          this.updateProgress(2); // Still count this as a step
        }
    },

    async generateInitialRealmLore(realmDbRef) {
        // Optional: Generate a brief lore summary for new realms
        try {
            const lorePrompt = `
            For the realm "${this.realm.name}" (Concept: ${this.realm.concept}), provide a short lore summary (1 paragraph) and 3-5 key rules or principles that govern this world.
            Output JSON: {"short_lore": "string", "rules": "string (numbered list)"}
            `;
            await this._executeLlmStep(lorePrompt, "Initial Realm Lore",
                (data) => {
                    if (!this.realm.lore) this.realm.lore = { short: "", detailed: "", history: "", rules: "" };
                    this.realm.lore.short = data.short_lore || "";
                    this.realm.lore.rules = data.rules || "";
                },
                () => ({ lore: this.realm.lore }),
                realmDbRef, 3 // Lore panel index (adjust if panel structure changes)
            );
        } catch (error) {
            console.warn("Failed to generate initial realm lore:", error.message);
            // Non-critical, so don't let it break the main flow
        }
    },


    // --- Existing Methods (may need minor adjustments or can be reused) ---
    async fetchRealms() {
      // console.log("Fetching realms...");
      const wasLoading = this.loading; // Preserve main loading state
      this.loading = true; // Indicate loading for realm list
      this.realms = [];
      try {
        const realmsRef = collection(db, 'realms');
        const querySnapshot = await getDocs(realmsRef);
        querySnapshot.forEach((doc) => {
          let realmData = doc.data();
          realmData.id = doc.id;
          // Ensure nested structures exist for older data
          if (!realmData.landscape) realmData.landscape = { image: '', images: [] };
          if (!realmData.landscape.images) realmData.landscape.images = [];
          if (!realmData.characters) realmData.characters = [];
           realmData.characters.forEach(c => {
             if (!c.avatar) c.avatar = { image: '', images: []};
             if (!c.avatar.images) c.avatar.images = [];
           });
          this.realms.push(realmData);
        });
      } catch (error) {
        console.error("Error fetching realms:", error);
        this.errorMessage = "Could not load existing realms.";
      } finally {
        this.loading = wasLoading; // Restore main loading state
      }
    },
    
    checkForExistingRealmRouteParam() { // Renamed from checkForExistingRealm
      const realmId = this.$route.params.id;
      if (realmId) {
        // This function is for when user navigates directly to /realm/:id
        // We should load this realm and set it as the 'selectedExistingRealm'
        // and currentPath to 'existing'
        this.loadRealmByIdForEditing(realmId);
      }
    },

    async loadRealmByIdForEditing(realmId) {
        this.loading = true;
        try {
            const realmDoc = await getDoc(doc(db, 'realms', realmId));
            if (realmDoc.exists()) {
                const loadedData = { ...realmDoc.data(), id: realmDoc.id };
                
                // Ensure nested structures for safety
                if (!loadedData.landscape) loadedData.landscape = { image: '', images: [] };
                if (!loadedData.landscape.images) loadedData.landscape.images = [];
                if (loadedData.landscape.image && !loadedData.landscape.images.includes(loadedData.landscape.image)) {
                    loadedData.landscape.images.unshift(loadedData.landscape.image);
                }
                if (!loadedData.characters) loadedData.characters = [];
                loadedData.characters.forEach(character => {
                    if (!character.avatar) character.avatar = { image: '', images: [] };
                    if (!character.avatar.images) character.avatar.images = [];
                     if (character.avatar.image && !character.avatar.images.includes(character.avatar.image)) {
                        character.avatar.images.unshift(character.avatar.image);
                    }
                });
                if (!loadedData.lore) loadedData.lore = { short: "", detailed: "", history: "", rules: "" };
                if (!loadedData.story) loadedData.story = { content: "", audio: "" };


                this.selectedExistingRealm = JSON.parse(JSON.stringify(loadedData));
                this.currentPath = 'existing';
                this.realm = loadedData; // Directly load into main realm object for display
                this.isEditMode = true;
                this.username = this.authUser?.displayName || "Player"; // Default username if navigating directly
                
                // Open relevant panels
                this.panels = [0,1]; // Basic Info, Landscape
                if (this.realm.characters.length > 0) this.panels.push(2);
                if (this.realm.lore.short) this.panels.push(3);
                if (this.realm.story.content) this.panels.push(4);
                if (this.panels.length === 0) this.panels = [0];

            } else {
                this.errorMessage = 'Realm not found from URL parameter.';
                this.$router.push('/'); // Or to your realm creation page
            }
        } catch (error) {
            console.error('Error fetching realm by ID:', error);
            this.errorMessage = `Error loading realm: ${error.message}`;
        } finally {
            this.loading = false;
        }
    },
    
    async callHttpFunction(url, payload, loadingFlagKey, resultPropKey, errorPropKey, successCallback, errorCallback, isBinaryResponse = false) {
      if (loadingFlagKey) this[loadingFlagKey] = true;
      // Removed resultPropKey and errorPropKey direct assignments, rely on callbacks or thrown errors
      // if (resultPropKey) this[resultPropKey] = null; 
      // if (errorPropKey) this[errorPropKey] = null;   

      try {
        if (!this.authUser) throw new Error("User not authenticated for HTTP call.");
        const token = await getIdToken(this.authUser);
        
        const isAudioGeneration = url.includes('generateAudio'); // More robust check
        if (isAudioGeneration) isBinaryResponse = true; 
        
        const response = await fetch(url, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`, 
            'Content-Type': 'application/json',
            'Accept': isAudioGeneration ? 'audio/mpeg' : 'application/json'
          },
          body: JSON.stringify(payload) 
        });

        if (!response.ok) {
          let errorData = { message: `HTTP error! status: ${response.status}` };
          try { 
            const errText = await response.text(); 
            console.error("Error response text:", errText.substring(0, 500));
            const errJson = JSON.parse(errText); 
            errorData.message = errJson.error?.message || errJson.error || errJson.message || errText || errorData.message;
          } catch (e) { 
            errorData.message = response.statusText || errorData.message; 
          }
          throw new Error(errorData.message);
        }

        let responseData;
        if (isBinaryResponse) {
          responseData = await response.blob();
          if (isAudioGeneration) {
            responseData = URL.createObjectURL(responseData);
          }
        } else {
          const textResponse = await response.text(); 
          try { 
            responseData = JSON.parse(textResponse); 
          } 
          catch (e) { 
            console.warn("Response was not JSON, returning as text:", textResponse.substring(0,100));
            responseData = textResponse; 
          }
        }
        
        if (successCallback) successCallback(responseData);
        return responseData; // Return data for await patterns

      } catch (error) {
        console.error(`Error calling ${url}:`, error);
        const wrappedError = new Error(`Failed to call ${url}: ${error.message}`);
        if (errorCallback) errorCallback(wrappedError);
        else throw wrappedError; 
      } finally {
        if (loadingFlagKey) this[loadingFlagKey] = false;
      }
    },

    _extractJsonFromString(text) {
      if (typeof text === 'object' && text !== null) return text; 
      if (typeof text !== 'string') {
        console.warn("_extractJsonFromString: Invalid input type:", typeof text);
        throw new Error("Invalid input type for JSON extraction.");
      }
      const markdownMatch = text.match(/```json\s*([\s\S]*?)\s*```/im);
      if (markdownMatch && markdownMatch[1]) {
        try { return JSON.parse(markdownMatch[1]); } 
        catch (e) { console.warn("_extractJsonFromString: Failed to parse Markdown block:", e.message); }
      }
      const firstBrace = text.indexOf('{');
      const lastBrace = text.lastIndexOf('}');
      if (firstBrace !== -1 && lastBrace > firstBrace) {
        const potentialJson = text.substring(firstBrace, lastBrace + 1);
        try { return JSON.parse(potentialJson); } 
        catch (e) { console.warn(`_extractJsonFromString: Failed to parse substring: ${e.message}.`); }
      }
      try { return JSON.parse(text); } 
      catch (e) {
        console.error("_extractJsonFromString: Failed after all attempts. Error:", e.message, "\nText:", text.substring(0,300));
        throw new Error(`LLM response was not valid JSON. Raw: ${text.substring(0,100)}...`);
      }
    },

    removeCharacter(index) {
      if (this.realm.characters[index]?.name === this.username) {
        this.errorMessage = "Cannot remove your main character generated in this session.";
        setTimeout(() => this.errorMessage = "", 3000);
        return;
      }
      this.realm.characters.splice(index, 1);
      // No progress update needed as this is a manual action after generation
      // If you want to save this change immediately:
      // if (this.realm.id) this.updateRealm(); 
    },

    updateProgress(autoOpenNextPanelIndex = -1) {
      this.completedSteps++;
      if (this.totalSteps > 0) {
        this.progressValue = Math.min((this.completedSteps / this.totalSteps) * 100, 100);
      }
      if (autoOpenNextPanelIndex !== -1 && autoOpenNextPanelIndex < 5 && !this.panels.includes(autoOpenNextPanelIndex)) { 
          // Ensure panel index is valid (0:Basic, 1:Landscape, 2:Character, 3:Lore, 4:Story)
        this.panels.push(autoOpenNextPanelIndex);
      }
    },

    async _executeLlmStep(prompt, stepDescription, processFn, dbUpdateDataFn, realmDbRef, autoOpenPanelIndex) {
      try {
        if (!this.authUser) throw new Error("User not authenticated");
        if (!this.realm.id && !realmDbRef) throw new Error("No realm ID or DbRef for LLM step");
        const currentRealmDbRef = realmDbRef || doc(db, 'realms', this.realm.id);

        console.log(`Executing LLM Step: ${stepDescription}`);
        // this.errorMessage = ''; // Clear global error, let local errors be handled
        
        const fullPrompt = `You are an AI assistant that STRICTLY outputs JSON.
Do NOT include any Markdown formatting (like \`\`\`json ... \`\`\`), any explanatory text, comments, or any conversational fluff before or after the JSON object.
The entire response MUST be a single, valid JSON object.
${prompt}`;
        
        let rawLlmResponse = null;
        await this.callHttpFunction(
          this.$endpoints.invokeLLM,
          { provider: 'anthropic', tier: 'fast', stream: false, prompt: fullPrompt }, 
          null, null, null, // No specific loading/result/error props here, handled by try/catch
          (data) => { rawLlmResponse = data; },
          (error) => { throw error; } // Re-throw to be caught by outer try/catch
        );

        if (rawLlmResponse === null) throw new Error(`No data received from LLM for ${stepDescription}.`);
        
        let jsonData = this._extractJsonFromString(rawLlmResponse);
        processFn(jsonData); // This updates this.realm properties
        
        const updatePayload = dbUpdateDataFn(); // This gets data from this.realm
        await updateDoc(currentRealmDbRef, { ...updatePayload, updatedAt: serverTimestamp() });
        this.updateProgress(autoOpenPanelIndex);
        console.log(`Completed LLM Step: ${stepDescription}`);
      } catch (error) {
        const errorMessageText = error.message || 'Unknown error';
        console.error(`Error in LLM step "${stepDescription}": ${errorMessageText}`);
        // this.errorMessage = `Failed ${stepDescription}: ${errorMessageText}`; // Set global error
        throw new Error(`Failed ${stepDescription}: ${errorMessageText}`); // Propagate error
      }
    },

    async _executeImageStep(promptText, imageFileNameSeed, imageTargetSetterFn, dbUpdateDataFn, realmDbRef, autoOpenPanelIndex) {
      const description = imageFileNameSeed;
      console.log(`Executing Image Step: ${description} (prompt: "${promptText ? promptText.substring(0,50)+'...' : 'NO PROMPT'}")`);
      // this.errorMessage = '';

      if (!promptText || promptText.trim() === "") {
        console.warn(`Skipping image generation for ${description} due to empty prompt.`);
        this.updateProgress(autoOpenPanelIndex); // Still count as a step for progress
        imageTargetSetterFn(""); // Ensure target is cleared or set to empty
        // Update DB if dbUpdateDataFn provides meaningful empty state
        const updatePayload = dbUpdateDataFn();
        if (Object.keys(updatePayload).length > 0) {
            await updateDoc(realmDbRef, { ...updatePayload, updatedAt: serverTimestamp() });
        }
        return;
      }

      if ((!this.realm.id && !realmDbRef) || !this.authUser) {
        throw new Error(`Cannot generate/save image for ${description}: Realm ID/DbRef or user auth missing.`);
      }
      const currentRealmDbRef = realmDbRef || doc(db, 'realms', this.realm.id);
      const currentRealmId = this.realm.id || currentRealmDbRef.id;


      let generatedImageData = null;
      try {
        await this.callHttpFunction(
          this.$endpoints.generateImage, // Ensure this endpoint is configured
          { prompt: promptText.trim() }, null, null, null,
          (data) => { generatedImageData = data; },
          (error) => { throw error; }
        );
      } catch (genError) {
        console.error(`Image generation API call for ${description} failed: ${genError.message}`);
        // Don't throw yet, try to proceed with empty image if needed, or let setter handle it
        imageTargetSetterFn(""); // Set to empty on failure
      }

      let finalStorageUrl = null;
      if (generatedImageData) {
        try {
          const imageFileExtension = 'png'; // Assuming PNG from b64_json or data_url
          const imageName = `${imageFileNameSeed.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}.${imageFileExtension}`;
          const filePath = `public/realms/${currentRealmId}/${imageName}`;
          const imageStorageRef = storageFileRef(this.storage, filePath);

          let uploadResult;
          if (typeof generatedImageData === 'string' && generatedImageData.startsWith('data:image')) {
            uploadResult = await uploadString(imageStorageRef, generatedImageData, 'data_url');
          } else if (typeof generatedImageData === 'object' && generatedImageData.b64_json) {
            const dataUrl = `data:image/png;base64,${generatedImageData.b64_json}`;
            uploadResult = await uploadString(imageStorageRef, dataUrl, 'data_url');
          } else if (typeof generatedImageData === 'string' && (generatedImageData.startsWith('http:') || generatedImageData.startsWith('https:'))) {
            // If it's a direct URL to an image (less common for generation functions but possible)
            const response = await fetch(generatedImageData);
            if (!response.ok) throw new Error(`Failed to fetch image from URL: ${response.statusText}`);
            const blob = await response.blob();
            uploadResult = await uploadBytes(imageStorageRef, blob);
          } else {
            throw new Error('Unsupported image data format received from generation API.');
          }
          finalStorageUrl = await getDownloadURL(uploadResult.ref);
        } catch (uploadError) {
          console.error(`Failed to upload/get URL for ${description} image:`, uploadError);
          imageTargetSetterFn(""); // Set to empty on upload failure
        }
      }
      
      imageTargetSetterFn(finalStorageUrl || ""); // Update this.realm via the setter

      const updatePayload = dbUpdateDataFn(); // Get data from this.realm AFTER setter
      await updateDoc(currentRealmDbRef, { ...updatePayload, updatedAt: serverTimestamp() });
      
      this.updateProgress(autoOpenPanelIndex);
      console.log(`Completed Image Step: ${description} (Final URL: ${finalStorageUrl || 'N/A'})`);
    },
    
    async _executeAudioStep(text, description, audioFileNameSeed, audioTargetSetterFn, dbUpdateDataFn, realmDbRef, autoOpenPanelIndex) {
      // ... (This method implementation largely remains the same, ensure it uses realmDbRef and updates this.realm via audioTargetSetterFn)
      console.log(`Executing Audio Step: ${description} (text length: ${text ? text.length : 0})`);
      // this.errorMessage = '';

      if (!text || text.trim() === "") {
        console.warn(`Skipping audio generation for ${description} due to empty text.`);
        audioTargetSetterFn("");
        this.updateProgress(autoOpenPanelIndex);
        // Update DB if dbUpdateDataFn provides meaningful empty state
        const updatePayload = dbUpdateDataFn();
        if (Object.keys(updatePayload).length > 0) {
             await updateDoc(realmDbRef, { ...updatePayload, updatedAt: serverTimestamp() });
        }
        return;
      }

      if ((!this.realm.id && !realmDbRef) || !this.authUser) {
        throw new Error(`Cannot save audio for ${description}: Realm ID/DbRef or user auth missing.`);
      }
      const currentRealmDbRef = realmDbRef || doc(db, 'realms', this.realm.id);
      const currentRealmId = this.realm.id || currentRealmDbRef.id;


      let generatedAudioData = null; // This will be a blob URL from callHttpFunction
      this.generatingAudio = true; // Use specific flag if this is called outside main flow
      try {
        generatedAudioData = await this.callHttpFunction(
          this.$endpoints.generateAudio, // Ensure this endpoint is configured
          { input: text }, null, null, null,
          (data) => { /* success handled by returned data */ },
          (error) => { throw error; }
        );
      } catch (genError) {
        console.error(`Audio generation API call for ${description} failed: ${genError.message}`);
        audioTargetSetterFn("");
        this.generatingAudio = false;
        // this.errorMessage = `Failed to generate audio for ${description}: ${genError.message}`;
        // Don't re-throw here if we want to continue; db update will save empty audio URL
      }

      let finalStorageUrl = null;
      if (generatedAudioData) { // generatedAudioData is expected to be a blob URL
        try {
          const response = await fetch(generatedAudioData);
          if (!response.ok) throw new Error(`Failed to fetch audio blob from URL: ${response.statusText}`);
          const blob = await response.blob();
          
          const audioFileExtension = 'mp3';
          const audioName = `${audioFileNameSeed.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}.${audioFileExtension}`;
          const filePath = `public/realms/${currentRealmId}/${audioName}`;
          const audioStorageRef = storageFileRef(this.storage, filePath);
          
          const uploadResult = await uploadBytes(audioStorageRef, blob);
          finalStorageUrl = await getDownloadURL(uploadResult.ref);
          URL.revokeObjectURL(generatedAudioData); // Clean up blob URL
        } catch (uploadError) {
          console.error(`Failed to upload/get URL for ${description} audio:`, uploadError);
          audioTargetSetterFn("");
          // this.errorMessage = `Failed to upload audio for ${description}: ${uploadError.message}`;
        }
      }
      this.generatingAudio = false;

      audioTargetSetterFn(finalStorageUrl || ""); // Update this.realm property

      const updatePayload = dbUpdateDataFn(); // Get data from this.realm
      await updateDoc(currentRealmDbRef, { ...updatePayload, updatedAt: serverTimestamp() });
      this.updateProgress(autoOpenPanelIndex);
      console.log(`Completed Audio Step: ${description} (Final URL: ${finalStorageUrl || 'N/A'})`);
    },

    setMainImage(imageUrl) {
      this.realm.landscape.image = imageUrl;
      // Optional: if you want this to persist immediately:
      // if (this.isEditMode && this.realm.id) this.updateRealm();
    },

    setMainCharacterImage(characterIndex, imageUrl) {
      if (this.realm.characters[characterIndex]) {
        this.realm.characters[characterIndex].avatar.image = imageUrl;
        // Optional: if you want this to persist immediately:
        // if (this.isEditMode && this.realm.id) this.updateRealm();
      }
    },

    getRandomCharacterType() {
      const rand = Math.floor(Math.random() * 100);
      if (rand < 50) return "male human";
      else if (rand < 75) return "female human";
      else {
        const abstractTypes = ["elemental being", "construct or robot", "mythical creature", "anthropomorphic animal", "spirit entity"];
        return abstractTypes[Math.floor(Math.random() * abstractTypes.length)];
      }
    },

    async generateAdditionalCharacterImage(characterIndex) {
      // ... (This method can remain largely the same, ensure it uses this.generatingCharacterImage and this.generatingCharacterIndex)
      if (!this.authUser) { this.errorMessage = "Login required."; return; }
      if (!this.realm.id) { this.errorMessage = "Active realm required."; return; }
      
      const character = this.realm.characters[characterIndex];
      if (!character || !character.avatar.prompt) {
        this.errorMessage = "Character avatar prompt missing.";
        return;
      }
      
      this.generatingCharacterImage = true;
      this.generatingCharacterIndex = characterIndex;
      this.errorMessage = '';
      
      try {
        const realmDbRef = doc(db, 'realms', this.realm.id);
        await this._executeImageStep(
          character.avatar.prompt, // Use existing prompt
          `${character.name}_avatar_additional`,
          (url) => {
            if (url) {
              if (!character.avatar.images) character.avatar.images = [];
              character.avatar.images.push(url);
              if (!character.avatar.image) character.avatar.image = url; // Set as main if first
            }
          },
          () => ({ characters: this.realm.characters }), 
          realmDbRef, 
          -1 // No specific panel opening for this sub-action
        );
      } catch (error) {
        console.error('Error generating additional character image:', error);
        this.errorMessage = `Failed to generate additional avatar: ${error.message}`;
      } finally {
        this.generatingCharacterImage = false;
        this.generatingCharacterIndex = -1;
      }
    },

    async updateRealm() { // This is the general "Save Changes" button
      if (!this.authUser) { this.errorMessage = "Login required."; return; }
      if (!this.realm.id) { this.errorMessage = "No active realm to update."; return; }
      
      this.saving = true;
      this.errorMessage = '';
      try {
        const realmDbRef = doc(db, 'realms', this.realm.id);
        // Ensure all parts of realm are well-defined before saving
        const updatePayload = {
          idea: this.realm.idea || "",
          name: this.realm.name || "",
          concept: this.realm.concept || "",
          lore: this.realm.lore || { short: "", detailed: "", history: "", rules: "" },
          landscape: this.realm.landscape || { prompt:"", image: "", images: [] },
          story: this.realm.story || { content: "", audio: "" },
          characters: this.realm.characters || [],
          updatedAt: serverTimestamp()
        };
        
        await updateDoc(realmDbRef, updatePayload);
        this.errorMessage = 'Realm updated successfully!';
        setTimeout(() => this.errorMessage = "", 3000);
      } catch (error) {
        console.error('Error updating realm:', error);
        this.errorMessage = `Failed to update realm: ${error.message}`;
      } finally {
        this.saving = false;
      }
    },

    async generateNewCharacter(customRealmDbRef = null) { // For the "Generate Another Random Character" button
        // ... (This method generates a random character, not tied to this.username)
        // ... (It should use this.generatingCharacter flag)
      if (customRealmDbRef && customRealmDbRef.type === 'click') customRealmDbRef = null;
      if (!this.authUser) { this.errorMessage = "Login required."; return; }
      if (!this.realm.id) { this.errorMessage = "Active realm required."; return; }
      
      this.generatingCharacter = true;
      this.errorMessage = '';
      const realmDbRef = customRealmDbRef || doc(db, 'realms', this.realm.id);
      
      try {
        const existingNames = this.realm.characters.map(char => char.name.toLowerCase());
        const characterType = this.getRandomCharacterType();
        
        const charPrompt = `
        Generate a unique supporting character for realm "${this.realm.name}" (Concept: ${this.realm.concept}).
        This character is NOT the main protagonist.
        Their name MUST NOT be any of: ${existingNames.join(', ')}.
        Character type: ${characterType}.
        Output JSON: {
          "name": "string (Unique 2-word name, e.g., 'Silent Wanderer', 'Star Seer')",
          "origin": "string (1-2 paragraph origin story)",
          "lore": "string (1-2 paragraph lore, abilities, role in realm)",
          "role": "string (e.g., 'Mystic Guide', 'Ancient Guardian', 'Curious Inventor')",
          "avatar_prompt": "string (Detailed prompt for AI: shoulders-up portrait of this character, storybook style, matching description. Kid-friendly. Style: textured, painterly. Dimensions 1024x1024.)"
        }`;
        
        let charIndex = -1;
        let newCharacterData = { name: "", origin: "", lore: "", role: "Supporting Character", avatar: { prompt: "", image: "", images: [] }};

        await this._executeLlmStep(charPrompt, "New Random Character Details",
          (data) => {
            if (!data.name || !data.origin || !data.lore || !data.role || !data.avatar_prompt) throw new Error("Random Character JSON missing keys.");
            newCharacterData.name = data.name;
            // Ensure name is unique, add suffix if not (simple check)
            if (existingNames.includes(data.name.toLowerCase())) {
                newCharacterData.name = `${data.name} ${Math.floor(Math.random() * 100)}`;
            }
            newCharacterData.origin = data.origin;
            newCharacterData.lore = data.lore;
            newCharacterData.role = data.role;
            newCharacterData.avatar.prompt = data.avatar_prompt;
          },
          () => ({}), // DB update after image
          realmDbRef, -1 
        );
        
        this.realm.characters.push(newCharacterData);
        charIndex = this.realm.characters.length - 1;

        if (newCharacterData.avatar.prompt) {
          await this._executeImageStep(
            newCharacterData.avatar.prompt,
            newCharacterData.name.replace(/\s+/g, '_').toLowerCase() + "_avatar",
            (url) => {
              this.realm.characters[charIndex].avatar.image = url || "";
              if (url) this.realm.characters[charIndex].avatar.images = [url];
            },
            () => ({ characters: this.realm.characters }), 
            realmDbRef, -1 
          );
        } else {
          console.warn(`Skipping avatar for random character ${newCharacterData.name} due to missing prompt.`);
          await updateDoc(realmDbRef, { characters: this.realm.characters, updatedAt: serverTimestamp() });
        }
        if (!this.panels.includes(2)) this.panels.push(2); // Open character panel

      } catch (error) {
        console.error('Error generating new random character:', error);
        this.errorMessage = `Failed to generate new random character: ${error.message}`;
      } finally {
        this.generatingCharacter = false;
      }
    },

    async generateStoryAudio() { // For realm's story audio
      // ... (This method can remain largely the same, using this.generatingAudio)
      if (!this.authUser) { this.errorMessage = "Login required."; return; }
      if (!this.realm.id) { this.errorMessage = "Active realm required."; return; }
      if (!this.realm.story || !this.realm.story.content) {
        this.errorMessage = "No realm story content to narrate."; return;
      }
      this.generatingAudio = true; // Already a specific flag
      this.errorMessage = '';
      try {
        const realmDbRef = doc(db, 'realms', this.realm.id);
        await this._executeAudioStep(this.realm.story.content, "Realm Story Audio", "realm_story_narration",
          (url) => { this.realm.story.audio = url === null ? "" : url; },
          () => ({ 'story.audio': this.realm.story.audio }), 
          realmDbRef, 4); // Story panel index
      } catch (error) {
        console.error('Error generating realm story audio:', error);
        this.errorMessage = `Failed to generate realm story audio: ${error.message}`;
      } finally {
        this.generatingAudio = false;
      }
    },

    async generateAdditionalLandscapeImage() {
      // ... (This method can remain largely the same, using this.generatingLandscapeImage)
      if (!this.authUser) { this.errorMessage = "Login required."; return; }
      if (!this.realm.id) { this.errorMessage = "Active realm required."; return; }
      
      this.generatingLandscapeImage = true; // Already a specific flag
      this.errorMessage = '';
      try {
        const realmDbRef = doc(db, 'realms', this.realm.id);
        const landscapePrompt = this.realm.landscape.prompt || `A beautiful digital painting of ${this.realm.name}, ${this.realm.concept ? this.realm.concept.substring(0,100) : 'a wondrous place'}. Style: storybook, magical, fantasy art, cinematic lighting.`;
        
        await this._executeImageStep(
          landscapePrompt, "additional_landscape",
          (url) => {
            if (url) {
              if (!this.realm.landscape.images) this.realm.landscape.images = [];
              this.realm.landscape.images.push(url);
              if (!this.realm.landscape.image) this.realm.landscape.image = url; // Set as main if none exists
            }
          },
          () => ({ 'landscape.images': this.realm.landscape.images, 'landscape.image': this.realm.landscape.image }), 
          realmDbRef, -1 
        );
      } catch (error) {
        console.error('Error generating additional landscape image:', error);
        this.errorMessage = `Failed to generate additional landscape: ${error.message}`;
      } finally {
        this.generatingLandscapeImage = false;
      }
    },
    
    async triggerGenerateRealmStory() { // New method for the "Generate Full Realm Story" button
        if (!this.authUser) { this.errorMessage = "Login required."; return; }
        if (!this.realm.id || !this.realm.name || !this.realm.concept) {
            this.errorMessage = "Realm name and concept are required to generate a story."; return;
        }
        this.generatingRealmStory = true;
        this.errorMessage = '';
        try {
            const realmDbRef = doc(db, 'realms', this.realm.id);
            const storyPrompt = `
            Write a 5-7 paragraph kid-friendly fantasy short story set in "${this.realm.name}".
            Realm Concept: ${this.realm.concept}
            Realm Lore (if available): ${this.realm.lore.short || 'Not yet detailed.'}
            The story should be engaging, introduce a simple conflict or quest, and feature characters that fit the realm.
            Output JSON: {"story_content": "string (the story)"}
            `;
            await this._executeLlmStep(storyPrompt, "Full Realm Story",
                (data) => {
                    if (!this.realm.story) this.realm.story = { content: "", audio: "" };
                    this.realm.story.content = data.story_content || "";
                    this.realm.story.audio = ""; // Clear old audio if new story
                },
                () => ({ story: this.realm.story }),
                realmDbRef, 4 // Story panel index
            );
        } catch (error) {
            console.error('Error generating full realm story:', error);
            this.errorMessage = `Failed to generate realm story: ${error.message}`;
        } finally {
            this.generatingRealmStory = false;
        }
    },

    async generateCustomCharacter() {
      // ... (This method can remain largely the same, but use this.customCharacterError for its specific error display)
      // ... (It should use this.generatingCustomCharacter flag)
      this.customCharacterError = ''; // Clear specific error
      if (!this.$refs.customCharacterForm.validate()) return;
      if (!this.authUser) { this.customCharacterError = "Login required."; return; }
      if (!this.realm.id) { this.customCharacterError = "Active realm required to add character."; return; }
      
      this.generatingCustomCharacter = true;
      const realmDbRef = doc(db, 'realms', this.realm.id);

      try {
        const charPrompt = `
        Generate a character based on user input for realm "${this.realm.name}" (Concept: ${this.realm.concept}).
        User-defined Name: ${this.customCharacter.name}
        User-defined Description: ${this.customCharacter.description}
        Expand on this to create a full character.
        Output JSON: {
          "origin": "string (Origin story incorporating user description)",
          "lore": "string (Lore, abilities, role - expanded from user description)",
          "role": "string (A suitable role title)",
          "avatar_prompt": "string (Detailed prompt for AI: shoulders-up portrait of ${this.customCharacter.name}, storybook style, matching description. Kid-friendly. Style: textured, painterly. Dimensions 1024x1024.)"
        }`;
        
        let charIndex = -1;
        let newCharacterData = { 
            name: this.customCharacter.name, // Use user's name
            origin: "", lore: "", role: "", 
            avatar: { prompt: "", image: "", images: [] }
        };

        await this._executeLlmStep(charPrompt, "Custom Character Details",
          (data) => {
            if (!data.origin || !data.lore || !data.role || !data.avatar_prompt) throw new Error("Custom Character JSON missing keys.");
            newCharacterData.origin = data.origin;
            newCharacterData.lore = data.lore;
            newCharacterData.role = data.role;
            newCharacterData.avatar.prompt = data.avatar_prompt;
          },
          () => ({}), realmDbRef, -1
        );
        
        this.realm.characters.push(newCharacterData);
        charIndex = this.realm.characters.length - 1;

        if (newCharacterData.avatar.prompt) {
          await this._executeImageStep(
            newCharacterData.avatar.prompt,
            newCharacterData.name.replace(/\s+/g, '_').toLowerCase() + "_avatar",
            (url) => {
              this.realm.characters[charIndex].avatar.image = url || "";
              if (url) this.realm.characters[charIndex].avatar.images = [url];
            },
            () => ({ characters: this.realm.characters }), 
            realmDbRef, -1
          );
        } else {
          console.warn(`Skipping avatar for custom character ${newCharacterData.name} due to missing prompt.`);
          await updateDoc(realmDbRef, { characters: this.realm.characters, updatedAt: serverTimestamp() });
        }
        
        this.customCharacterDialog = false;
        this.customCharacter = { name: "", description: "" }; // Reset form
        if (!this.panels.includes(2)) this.panels.push(2); // Open character panel

      } catch (error) {
        console.error('Error generating custom character:', error);
        this.customCharacterError = `Failed: ${error.message}`; // Display error in dialog
      } finally {
        this.generatingCustomCharacter = false;
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.main-image-border {
  border: 2px solid #42A5F5; /* Vuetify blue */
}
.outfit-regular { /* Ensure this class is globally available or defined */
    font-family: 'Outfit', sans-serif;
}
</style>